/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { compose } from 'recompose';

import type { InjectedRouter } from 'js/lib/connectToRouter';
import connectToRouter from 'js/lib/connectToRouter';
import user from 'js/lib/user';

import { Grid, PageGridContainer, useTheme } from '@coursera/cds-core';

import { closeMiniModalWithRoute, useRouterParams } from 'bundles/enterprise-legacy-xdp/components/ProgramMiniModal';
import { getProductPath, launchMiniModal } from 'bundles/enterprise-legacy-xdp/hocs/withMiniModal';
import type EnterpriseProgramsV1 from 'bundles/naptimejs/resources/enterprisePrograms.v1';
import type ThirdPartyOrganizationsV1 from 'bundles/naptimejs/resources/thirdPartyOrganizations.v1';
import ExperimentalProgramFullScreenModal from 'bundles/program-common/components/ExperimentalProgramFullScreenModal';
import type { Product, ProductType } from 'bundles/program-common/types/programCommon';
import ProgramHomeWrapper from 'bundles/program-home/components/ProgramHomeWrapper';
import ObjectivesBreadcrumb from 'bundles/program-skills-objectives/components/ObjectivesBreadcrumb/ObjectivesBreadcrumb';
import SkillPageContent from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/SkillPageContent';
import type { PropsFromGetSkillsQuery } from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/withSkillDetails';

import _t from 'i18n!nls/program-skills-objectives';

import withSkillDetails from './withSkillDetails';

type PropsFromRouter = {
  router: InjectedRouter;
  programSlug: string;
  objectiveSlug: string;
  skillId: string;
};

type PropsFromCaller = {
  userId: number;
  thirdPartyOrganizationId: string;
  programId: string;
  program: EnterpriseProgramsV1;
  thirdPartyOrganization: ThirdPartyOrganizationsV1;
  isProgramMember: boolean;
  isProgramAdmin: boolean;
  isBrowseOnlyProgram: boolean;
  shouldShowSponsoredByMessage: boolean;
  enableSkillsInSearchAndBrowse: boolean;
  enableIntroClipsForSkillPages?: boolean;
};

export type Props = PropsFromCaller & PropsFromRouter & PropsFromGetSkillsQuery;

/* This page displays course recommendations for a given skill outside of a skillset. */
export const ProgramSkillRecommendationsPage: React.FC<Props> = ({
  programSlug,
  thirdPartyOrganization,
  thirdPartyOrganizationId,
  programId,
  program,
  isProgramMember,
  isProgramAdmin,
  enableSkillsInSearchAndBrowse,
  router,
  isBrowseOnlyProgram,
  shouldShowSponsoredByMessage,
  skillId,
  skill,
}) => {
  const theme = useTheme();
  const routerParams = useRouterParams(router);
  const onProductCardClick = (product: Product, productType: ProductType, collectionId?: string | null) => {
    if (productType === 'clip') {
      window.open(`/videos/${product.slug}`, '_blank');
    } else {
      launchMiniModal(router, product.id, productType === 'course', collectionId || '', undefined, undefined, {
        slug: product.slug,
        productPagePath: getProductPath(product),
      });
    }
  };

  const programName = program?.metadata?.name ?? _t('My Learning Program');
  const userId = user.get().id;

  const styles = {
    page: css`
      background-color: ${theme.palette.white};
    `,
    container: css`
      margin: 0 auto;
    `,
  };

  return (
    <ProgramHomeWrapper
      className="rc-ProgramSkillRecommendationsPage"
      thirdPartyOrganizationId={thirdPartyOrganizationId}
      thirdPartyOrganizationSlug={thirdPartyOrganization?.slug}
      program={program}
      programId={programId}
      programName={programName}
      isAuthenticatedUser={!!userId}
      css={styles.page}
      enableSkillsInSearchAndBrowse={enableSkillsInSearchAndBrowse}
    >
      {routerParams && (
        <ExperimentalProgramFullScreenModal
          thirdPartyOrganizationSlug={thirdPartyOrganization.slug}
          program={program}
          thirdPartyOrganizationName={thirdPartyOrganization.name}
          isProgramMember={isProgramMember}
          isProgramAdmin={isProgramAdmin}
          programId={programId}
          renderCourseHomeLink
          onCloseMiniModal={() => closeMiniModalWithRoute(router)}
          isUpswell={false}
          hideAction={isBrowseOnlyProgram}
          disableSponsoredByMessage={!shouldShowSponsoredByMessage}
          breadcrumb={
            <ObjectivesBreadcrumb
              programSlug={programSlug}
              thirdPartyOrgSlug={thirdPartyOrganization.slug}
              thirdPartyOrganizationId={thirdPartyOrganization.id}
              programName={programName}
              skillName={skill?.skillName ?? skillId}
              skillId={skillId}
              showSkillDashboard
            />
          }
          {...routerParams}
        />
      )}
      <main css={styles.container}>
        <PageGridContainer>
          <Grid item xs={12}>
            <SkillPageContent
              /* key ensures component is remounted when skill Id changes which is needed to reset state */
              key={skillId}
              programId={programId}
              skillId={skillId}
              onProductCardClick={onProductCardClick}
              userId={userId}
              programSlug={programSlug}
              thirdPartyOrganization={thirdPartyOrganization}
              programName={programName}
              isAuthenticatedUser={!!userId}
            />
          </Grid>
        </PageGridContainer>
      </main>
    </ProgramHomeWrapper>
  );
};

export const enhance = compose<Props, PropsFromCaller>(
  connectToRouter<PropsFromRouter, {}>((router) => ({
    router,
    programSlug: router.params.programSlug,
    objectiveSlug: router.params.objectiveSlug,
    skillId: router.params.skillId,
  })),
  withSkillDetails
);

export default enhance(ProgramSkillRecommendationsPage);
